import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTranslation } from 'react-i18next';

import logo from '../../../images/logo25.svg';
import { appStore } from '../../../stores/AppStore';
import { loadingProgressStore } from '../../../stores/LoadingProgressStore';
import theme from '../../../styles/theme';
import { Watermark } from '../../ViewerContainer/bottomInfo/Watermark';

import { CircularProgressPanel } from './CircularProgressPanel';

import { makeStyles } from '@egr/xbox/utils/tss-react';

const useStyles = makeStyles({ name: 'ProgressPanel' })({
    backdrop: {
        backgroundColor: 'white',
        zIndex: 1301,
    },
    container: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '292px',
        gap: '16px',
    },
    header: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        width: '100%',
    },
    progressContainer: {
        width: '100%',
    },
    progress: {
        background: '#ECECEC',
        height: '6px',
        borderRadius: '10px',
    },
    remaining: {
        alignSelf: 'flex-start',
        minHeight: 20,
    },
    error: {
        textAlign: 'center',
    },
    logoContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        width: 138,
        paddingBottom: '34px',
    },

    logo: {
        width: 36,
        height: 36,
    },

    title: {
        fontSize: '24px',
        color: theme.palette.text.primary,
        paddingLeft: '18px',
    },
    text: {
        color: '#212121',
    },
    bottomText: {
        fontSize: '12px',
    },
    progressGroup: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },
});

export const ProgressPanel: React.FC = () => {
    const { classes } = useStyles();
    const { t } = useTranslation();
    const { active, steps, error, targetPercent, estimatedTime } = loadingProgressStore.useState([
        'active',
        'steps',
        'error',
        'targetPercent',
        'estimatedTime',
    ]);
    const { embed } = appStore.useState(['embed']);
    const smallWidth = useMediaQuery('(max-width:299px)');
    const smallHeight = useMediaQuery('(max-height:299px)');
    const isSmallScreen = smallWidth || smallHeight;

    if (!active) {
        return null;
    }

    const runningStep = steps.find((s) => s.status === 'running') ?? [...steps].reverse().find((s) => s.status === 'done');
    const message = runningStep != null ? runningStep.label : '';

    if (isSmallScreen && embed) {
        return (
            <CircularProgressPanel
                open={active}
                percent={targetPercent}
            />
        );
    }

    return (
        <Backdrop
            open={active}
            className={classes.backdrop}
        >
            <Box className={classes.container}>
                {embed && (
                    <Box className={classes.logoContainer}>
                        <img
                            src={logo}
                            className={classes.logo}
                        />
                        <Typography className={classes.title}>impress</Typography>
                    </Box>
                )}
                <Box className={classes.header}>
                    <Typography className={classes.text}>{message}</Typography>
                    <Typography className={classes.text}>{typeof targetPercent === 'number' ? `${targetPercent}%` : ''}</Typography>
                </Box>
                <Box className={classes.progressGroup}>
                    <Box className={classes.progressContainer}>
                        <LinearProgress
                            color="secondary"
                            variant="determinate"
                            value={targetPercent ?? undefined}
                            className={classes.progress}
                        />
                    </Box>
                    <Box className={classes.remaining}>
                        {error ? (
                            <Typography
                                variant="body2"
                                color="error"
                                className={classes.error}
                            >
                                {t('Error')}: {error}
                            </Typography>
                        ) : (
                            estimatedTime && (
                                <Typography className={classes.bottomText}>
                                    {estimatedTime.minutes === 0
                                        ? t('{{seconds}} sec remaining', { seconds: estimatedTime.seconds })
                                        : t('{{minutes}} min {{seconds}} sec remaining', {
                                              minutes: estimatedTime.minutes,
                                              seconds: estimatedTime.seconds,
                                          })}
                                </Typography>
                            )
                        )}
                    </Box>
                </Box>
                {embed && <Watermark />}
            </Box>
        </Backdrop>
    );
};
